import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

export default function Home() {
  return (
    <div className="min-h-screen bg-cream font-sans">
      <Nav />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="pt-28 lg:pt-36 pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: copy */}
            <div>
              <p className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase mb-6">
                AI Automation Consultancy
              </p>
              <h1 className="font-display text-5xl lg:text-[3.75rem] font-bold text-charcoal leading-[1.1] mb-7">
                Enterprise-Grade AI Automation. Built for Growing Businesses.
              </h1>
              <p className="text-warm-gray text-lg leading-relaxed mb-10 max-w-lg">
                With over 25 years at Oracle, Level 3 Communications, Global
                Crossing, Avaya, and Lucent Technologies — including 11 years
                as a Principal Sales Consultant — I build AI automation systems
                that eliminate manual work, capture every lead, and scale with
                your business.
              </p>
              <Link
                to="/audit"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-4 text-base transition-colors"
              >
                Get Your Free AI Audit <ArrowRight size={18} />
              </Link>
            </div>

            {/* Right: headshot */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative w-full max-w-sm">
                {/* Decorative amber offset */}
                <div className="absolute top-5 right-0 bottom-0 left-5 bg-amber-100" />
                <img
                  src="/headshot.jpg"
                  alt="Josh — Founder, Jambition"
                  className="relative w-full object-cover object-top"
                  style={{ aspectRatio: '3 / 4' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pain Points ──────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="w-10 h-0.5 bg-amber-600 mb-7" />
            <h2 className="font-display text-4xl font-bold text-charcoal mb-4">
              The challenges holding your business back.
            </h2>
            <p className="text-warm-gray text-lg leading-relaxed">
              You're not short on ambition. You're short on time, systems, and
              leverage. These are the problems Jambition is built to solve.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                number: '01',
                title: 'Too much manual work, not enough time.',
                body: 'Your team is stuck doing the same repetitive tasks every week — data entry, follow-ups, reporting. You know it should be automated. You just haven\'t had time to make it happen.',
              },
              {
                number: '02',
                title: 'Leads are falling through the cracks.',
                body: 'Prospects come in, but without a reliable follow-up system, too many go cold. Every missed lead is revenue your business will never recover.',
              },
              {
                number: '03',
                title: 'You can\'t scale without hiring.',
                body: 'Growth means more work, which means more headcount. But headcount is expensive, slow to hire, and hard to manage. There\'s a better way to grow.',
              },
            ].map((item) => (
              <div
                key={item.number}
                className="border-t-2 border-warm-border pt-8"
              >
                <p className="font-display font-bold text-3xl text-amber-600 mb-4">
                  {item.number}
                </p>
                <h3 className="font-display text-xl font-bold text-charcoal mb-3">
                  {item.title}
                </h3>
                <p className="text-warm-gray leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services ─────────────────────────────────────── */}
      <section className="py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="w-10 h-0.5 bg-amber-600 mb-7" />
            <h2 className="font-display text-4xl font-bold text-charcoal mb-4">
              What Jambition Builds
            </h2>
            <p className="text-warm-gray text-lg leading-relaxed">
              Every system we build is custom — designed for your workflow, your
              team, and your growth stage. No off-the-shelf tools.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Custom Automation Development',
                body: 'We map your manual workflows and build systems that run without you — eliminating repetitive work and letting your team focus on what actually drives revenue.',
                href: '/services#custom-automation',
              },
              {
                title: 'Outreach Systems',
                body: 'Automated, personalized outreach pipelines that fill your calendar — from prospect identification to reply handling — without adding headcount.',
                href: '/services#outreach-systems',
              },
              {
                title: 'Lead Generation',
                body: 'Signal-based prospecting that finds the right prospects at the right moment — job changes, funding events, trigger signals — and routes them directly into your pipeline.',
                href: '/services#lead-generation',
              },
            ].map((service) => (
              <div
                key={service.title}
                className="bg-white p-8 border border-warm-border group"
              >
                <h3 className="font-display text-xl font-bold text-charcoal mb-4">
                  {service.title}
                </h3>
                <p className="text-warm-gray leading-relaxed mb-7">
                  {service.body}
                </p>
                <Link
                  to={service.href}
                  className="inline-flex items-center gap-1.5 text-amber-600 text-sm font-semibold group-hover:gap-3 transition-all duration-200"
                >
                  Learn more <ArrowRight size={15} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Josh ─────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-10 h-0.5 bg-amber-600 mb-7" />
            <h2 className="font-display text-4xl font-bold text-charcoal mb-8">
              Why Jambition
            </h2>

            <blockquote className="border-l-4 border-amber-600 pl-7 mb-8">
              <p className="font-display text-xl italic text-charcoal leading-relaxed">
                "Before founding Jambition, I spent over 25 years in enterprise
                technology — starting in telecom at Lucent Technologies and
                Avaya, engineering critical infrastructure at Global Crossing
                and Level 3 Communications, then 11 years as a Principal Sales
                Consultant at Oracle."
              </p>
            </blockquote>

            <p className="text-warm-gray text-lg leading-relaxed mb-4">
              I've seen how the world's largest companies solve complex
              operational problems. Now I bring that same rigor to businesses
              that are ready to grow — without the enterprise price tag or
              twelve-month timeline.
            </p>
            <p className="text-warm-gray text-lg leading-relaxed">
              When you work with Jambition, you work directly with me. No
              account managers, no junior consultants handed the work. Just
              focused execution from someone who has done this at the highest
              level.
            </p>

            {/* Stat row */}
            <div className="grid sm:grid-cols-3 gap-6 mt-12">
              {[
                { stat: '25+', label: 'Years in Enterprise Technology' },
                { stat: '11', label: 'Years as Oracle Principal Consultant' },
                { stat: '100%', label: 'Custom-Built Systems' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="border-t-2 border-warm-border pt-6"
                >
                  <p className="font-display text-4xl font-bold text-amber-600">
                    {item.stat}
                  </p>
                  <p className="text-sm text-warm-gray mt-1.5">{item.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Link
                to="/about"
                className="inline-flex items-center gap-1.5 text-charcoal font-semibold text-sm border-b border-charcoal pb-0.5 hover:text-amber-600 hover:border-amber-600 transition-colors"
              >
                Read the full story <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────── */}
      <section className="py-24 bg-amber-600">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-5">
            Ready to see what's possible?
          </h2>
          <p className="text-amber-100 text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            Start with a free AI audit of your business. No pitch, no pressure
            — just a clear picture of where automation can make the biggest
            difference.
          </p>
          <Link
            to="/audit"
            className="inline-flex items-center gap-2 bg-white text-amber-700 hover:bg-amber-50 font-bold px-9 py-4 text-base transition-colors"
          >
            Get Your Free AI Audit <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
